import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, DollarSign, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const ADMIN_USER_IDS = [
  '4ff6c99e-ae1e-43cc-911f-c87c73cf3efd',
  'f66fdff9-9ed7-4535-8791-cbf01bef2821'
];

export default function Cashout() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [paypalEmail, setPaypalEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    document.title = "Cashout - Soundope";
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: sessionResult } = await supabase.auth.getSession();
      const session = sessionResult?.session;
      
      if (!session || !session.user) {
        navigate("/auth");
        return;
      }

      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);
      
      const userIsAdmin = roles?.some(r => r.role === 'admin') || false;
      setIsAdmin(userIsAdmin);

      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();

      setCurrentUser(profile);
      
      const earnedCents = profile?.earned_credits || 0;
      const balanceUsd = (earnedCents / 100).toFixed(2);
      const minimumUsd = '15.00';
      const canCashoutValue = earnedCents >= 1500;
      
      setBalance({
        balance_usd: balanceUsd,
        review_earnings: earnedCents,
        minimum_usd: minimumUsd,
        can_cashout: canCashoutValue
      });

      const { data: historyData } = await supabase
        .from("cashout_requests")
        .select("*")
        .eq("user_id", session.user.id)
        .order("created_at", { ascending: false });

      if (historyData) {
        const mappedHistory = historyData.map(req => ({
          id: req.id,
          amount_usd: (req.amount_cents / 100).toFixed(2),
          status: req.status,
          requested_at: req.requested_at || req.created_at,
          admin_notes: req.admin_notes
        }));
        setHistory(mappedHistory);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load cashout data");
    } finally {
      setIsLoading(false);
    }
  };

  const canCashout = isAdmin ? (parseFloat(balance?.balance_usd || '0') > 0) : balance?.can_cashout;

  const handleCashout = async () => {
    if (!paypalEmail || !paypalEmail.includes('@')) {
      toast.error("Please enter a valid PayPal email");
      return;
    }

    if (!canCashout) {
      toast.error(`Minimum cashout is $${balance?.minimum_usd || '15.00'}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const amountCents = balance?.review_earnings || 0;
      const amountUsd = (amountCents / 100).toFixed(2);

      const { error: insertError } = await supabase
        .from('cashout_requests')
        .insert({
          user_id: currentUser.id,
          amount_cents: amountCents,
          paypal_email: paypalEmail,
          status: 'pending',
          requested_at: new Date().toISOString()
        });

      if (insertError) {
        throw new Error(insertError.message || 'Failed to submit cashout request');
      }

      const notificationPromises = ADMIN_USER_IDS.map(adminId =>
        supabase.from('notifications').insert({
          user_id: adminId,
          title: '💰 Cashout Request',
          message: `${currentUser?.username || 'A user'} requested a cashout of $${amountUsd} to ${paypalEmail}`,
          is_read: false
        })
      );
      
      Promise.all(notificationPromises).catch(err => 
        console.warn('Failed to notify admins:', err)
      );

      toast.success(`Cashout request submitted for $${amountUsd}!`);
      setPaypalEmail("");
      loadData();
    } catch (error) {
      console.error("Cashout error:", error);
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-yellow-500 border-yellow-500"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="text-blue-500 border-blue-500"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'paid':
        return <Badge variant="outline" className="text-green-500 border-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Paid</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="text-red-500 border-red-500"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasPendingRequest = history.some(h => h.status === 'pending');

  return (
    <div className="min-h-screen bg-background p-4 pb-24">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center py-6">
          <h1 className="text-2xl font-bold text-foreground">Cash Out Earnings</h1>
          <p className="text-muted-foreground text-sm mt-2">
            Withdraw the credits you've earned from reviews
          </p>
        </div>

        <Card className="neuro-base">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="w-5 h-5 text-green-500" />
              Your Earned Balance
            </CardTitle>
            <CardDescription>
              Credits earned from reviewing tracks (not purchased credits)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-foreground mb-2">
              ${balance?.balance_usd || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground">
              {balance?.review_earnings || 0} cents earned from reviews • Minimum cashout: ${balance?.minimum_usd || '15.00'}
            </p>
            
            {!canCashout && (
              <div className="mt-4 p-3 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground">
                    You need at least $15.00 in earned credits to cash out. Keep reviewing tracks to earn more!
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {canCashout && !hasPendingRequest && (
          <Card className="neuro-base">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Request Cashout</CardTitle>
              <CardDescription>
                Enter your PayPal email to receive payment
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                type="email"
                placeholder="your@paypal.email"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                className="neuro-pressed"
              />
              <Button
                onClick={handleCashout}
                disabled={isSubmitting || !paypalEmail}
                className="w-full neuro-base hover:neuro-pressed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <DollarSign className="w-4 h-4 mr-2" />
                    Cash Out ${balance?.balance_usd}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {hasPendingRequest && (
          <Card className="neuro-base border-yellow-500/30">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 text-yellow-500">
                <Clock className="w-5 h-5" />
                <span className="font-medium">You have a pending cashout request</span>
              </div>
            </CardContent>
          </Card>
        )}

        {history.length > 0 && (
          <Card className="neuro-base">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Cashout History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {history.map((request) => (
                  <div key={request.id} className="flex items-center justify-between p-3 rounded-lg neuro-flat">
                    <div>
                      <div className="font-medium text-foreground">${request.amount_usd}</div>
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(request.requested_at), 'MMM d, yyyy')}
                      </div>
                    </div>
                    {getStatusBadge(request.status)}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}